import {
  Component,
  ElementRef,
  Input,
  OnInit,
  ViewChild,
  forwardRef,
  ChangeDetectionStrategy,
} from '@angular/core';
import { FormBuilder, NG_VALUE_ACCESSOR } from '@angular/forms';

import { AppState } from 'src/app/reducers';
import { Observable } from 'rxjs';
import { Store } from '@ngrx/store';
import { User } from 'src/app/core/models/core.models';
import { selectUser } from 'src/app/core/reducers';

@Component({
  selector: 'app-chat-message-input',
  templateUrl: './chat-message-input.component.html',
  styleUrls: ['./chat-message-input.component.scss'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => ChatMessageInputComponent),
      multi: true,
    },
  ],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class ChatMessageInputComponent implements OnInit {
  // Supports both 'ltr' and 'rtl' layouts.
  @Input() direction: 'ltr' | 'rtl' = 'ltr';

  // Reference to the textarea element.
  @ViewChild('messageTextarea', { static: false })
  messageTextarea: ElementRef<HTMLTextAreaElement>;

  currentUser$: Observable<User>;

  value: string = '';

  // Callbacks for ControlValueAccessor
  onChange = (value: any) => {};
  onTouched = () => {};

  // Write a new value to the element.
  writeValue(value: any): void {
    this.value = value || '';
  }

  // Save the function as a callback to update the model.
  registerOnChange(fn: any): void {
    this.onChange = fn;
  }

  // Save the function as a callback to indicate a touch.
  registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }

  // Optionally, implement setDisabledState if needed.
  setDisabledState?(isDisabled: boolean): void {
    // For example, you can add a disabled property and bind it in your template.
  }

  // Called when the textarea value changes.
  onInput(event: Event): void {
    const target = event.target as HTMLTextAreaElement;
    this.value = target.value;
    this.onChange(this.value);
  }

  constructor(
    protected fb: FormBuilder,
    protected store: Store<AppState>,
  ) {
    this.currentUser$ = this.store.select(selectUser);
  }

  ngOnInit(): void {}

  // Inserts markdown syntax into the current message.
  /**
   * Wraps the currently selected text in the textarea with markdown syntax.
   * If no text is selected, inserts a placeholder text.
   */
  insertMarkdownSyntax(syntax: string): void {
    const textarea = this.messageTextarea?.nativeElement;
    if (!textarea) return;

    const start = textarea.selectionStart;
    const end = textarea.selectionEnd;
    const selectedText = textarea.value.substring(start, end);
    let formattedText = '';

    if (syntax === 'bold') {
      formattedText = `**${selectedText || 'bold text'}**`;
    } else if (syntax === 'italic') {
      formattedText = `*${selectedText || 'italic text'}*`;
    } else if (syntax === 'link') {
      formattedText = `[${selectedText || 'link text'}](url)`;
    }

    // Update the value by replacing the selected text.
    const newValue =
      textarea.value.substring(0, start) +
      formattedText +
      textarea.value.substring(end);
    this.value = newValue;
    this.onChange(this.value);

    // Optionally, update the textarea's selection range after insertion.
    setTimeout(() => {
      textarea.focus();
      textarea.setSelectionRange(start, start + formattedText.length);
    });
  }
}
